import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ParentOverview from "@/pages/ParentOverview";
import ParentChildren from "@/pages/ParentChildren";
import ParentAbsences from "@/pages/ParentAbsences";
import ParentComments from "@/pages/ParentComments";
import StudentSubjects from "@/pages/StudentSubjects";
import ReportPrint from "@/pages/ReportPrint";
import TeacherDashboard from "@/pages/TeacherDashboard";
import TeacherStudent from "@/pages/TeacherStudent";
import TeacherLMS from "@/pages/TeacherLMS";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminTimetable from "@/pages/AdminTimetable";
import StudentDashboard from "@/pages/StudentDashboard";
import StudentTimetable from "@/pages/StudentTimetable";
import StudentGrades from "@/pages/StudentGrades";
import StudentLMS from "@/pages/StudentLMS";
import NotificationsSend from "@/pages/NotificationsSend";
import NotificationsInbox from "@/pages/NotificationsInbox";
import Profile from "@/pages/Profile";
import TeacherTimetable from "@/pages/TeacherTimetable";
import TeacherComments from "@/pages/TeacherComments";
import TeacherPlans from "@/pages/TeacherPlans";
import TeacherStudentPlans from "@/pages/TeacherStudentPlans";
import KujdestariDashboard from "@/pages/KujdestariDashboard";
import KujdestariMeetings from "@/pages/KujdestariMeetings";
import KujdestariDiscipline from "@/pages/KujdestariDiscipline";
import KujdestariEndOfYearReport from "@/pages/KujdestariEndOfYearReport";
import TeacherConsultations from "@/pages/TeacherConsultations";
import TeacherAssignments from "@/pages/TeacherAssignments";
import ParentConsultations from "@/pages/ParentConsultations";
import StudentAssignments from "@/pages/StudentAssignments";
import SecretaryDashboard from "@/pages/SecretaryDashboard";
import SecretaryClass from "@/pages/SecretaryClass";
import SecretaryStaff from "@/pages/SecretaryStaff";
import QualityDashboard from "@/pages/QualityDashboard";
import AdminAnalytics from "@/pages/AdminAnalytics";
import Calendar from "@/pages/Calendar";
import AdministrataDashboard from "@/pages/AdministrataDashboard";
import AdministrataStudents from "@/pages/AdministrataStudents";
import AdministrataAppointments from "@/pages/AdministrataAppointments";
import SigurimiDashboard from "@/pages/SigurimiDashboard";
import SigurimiIncident from "@/pages/SigurimiIncident";
import SigurimiReports from "@/pages/SigurimiReports";
import DrejtoreshaDashboard from "@/pages/DrejtoreshaDashboard";
import DrejtoreshaCollaborations from "@/pages/DrejtoreshaCollaborations";
import { Toaster } from "@/components/ui/sonner";

const Protected = ({ children, roles }) => {
    const { user, loading } = useAuth();
    if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Duke ngarkuar…</div>;
    if (!user) return <Navigate to="/login" replace />;
    if (roles && !roles.includes(user.role)) {
        const dest = user.role === "super_admin" ? "/admin"
            : user.role === "teacher" ? "/teacher"
            : user.role === "student" ? "/student"
            : user.role === "secretary" ? "/secretary"
            : user.role === "quality_coordinator" ? "/quality"
            : user.role === "administrata" ? "/administrata"
            : user.role === "sigurimi" ? "/sigurimi"
            : user.role === "drejtoresha" ? "/drejtoresha"
            : "/parent";
        return <Navigate to={dest} replace />;
    }
    return children;
};

const RootRedirect = () => {
    const { user, loading } = useAuth();
    if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Duke ngarkuar…</div>;
    if (!user) return <Navigate to="/login" replace />;
    const dest = user.role === "super_admin" ? "/admin"
        : user.role === "teacher" ? "/teacher"
        : user.role === "student" ? "/student"
        : user.role === "secretary" ? "/secretary"
        : user.role === "quality_coordinator" ? "/quality"
        : user.role === "administrata" ? "/administrata"
        : user.role === "sigurimi" ? "/sigurimi"
        : user.role === "drejtoresha" ? "/drejtoresha"
        : "/parent";
    return <Navigate to={dest} replace />;
};

function App() {
    return (
        <div className="App">
            <AuthProvider>
                <BrowserRouter>
                    <Routes>
                        <Route path="/" element={<RootRedirect />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/inbox" element={<Protected><NotificationsInbox /></Protected>} />
                        <Route path="/profile" element={<Protected><Profile /></Protected>} />
                        <Route path="/calendar" element={<Protected><Calendar /></Protected>} />
                        <Route path="/admin/analytics" element={<Protected roles={["super_admin", "secretary"]}><AdminAnalytics /></Protected>} />

                        <Route path="/parent" element={<Protected roles={["parent"]}><ParentOverview /></Protected>} />
                        <Route path="/parent/children" element={<Protected roles={["parent"]}><ParentChildren /></Protected>} />
                        <Route path="/parent/student/:id" element={<Protected roles={["parent"]}><StudentSubjects /></Protected>} />
                        <Route path="/parent/student/:id/absences" element={<Protected roles={["parent"]}><ParentAbsences /></Protected>} />
                        <Route path="/parent/student/:id/comments" element={<Protected roles={["parent"]}><ParentComments /></Protected>} />
                        <Route path="/parent/student/:id/report" element={<Protected roles={["parent"]}><ReportPrint /></Protected>} />
                        <Route path="/parent/consultations" element={<Protected roles={["parent"]}><ParentConsultations /></Protected>} />

                        <Route path="/teacher" element={<Protected roles={["teacher", "super_admin"]}><TeacherDashboard /></Protected>} />
                        <Route path="/teacher/timetable" element={<Protected roles={["teacher", "super_admin"]}><TeacherTimetable /></Protected>} />
                        <Route path="/teacher/comments" element={<Protected roles={["teacher", "super_admin"]}><TeacherComments /></Protected>} />
                        <Route path="/teacher/plans" element={<Protected roles={["teacher", "super_admin"]}><TeacherPlans /></Protected>} />
                        <Route path="/teacher/student-plans" element={<Protected roles={["teacher", "super_admin"]}><TeacherStudentPlans /></Protected>} />
                        <Route path="/teacher/student/:id" element={<Protected roles={["teacher", "super_admin", "administrata", "drejtoresha"]}><TeacherStudent /></Protected>} />
                        <Route path="/teacher/lms" element={<Protected roles={["teacher", "super_admin"]}><TeacherLMS /></Protected>} />
                        <Route path="/teacher/notify" element={<Protected roles={["teacher", "super_admin"]}><NotificationsSend /></Protected>} />
                        <Route path="/teacher/consultations" element={<Protected roles={["teacher", "super_admin"]}><TeacherConsultations /></Protected>} />
                        <Route path="/teacher/assignments" element={<Protected roles={["teacher", "super_admin"]}><TeacherAssignments /></Protected>} />
                        <Route path="/student/assignments" element={<Protected roles={["student"]}><StudentAssignments /></Protected>} />

                        <Route path="/kujdestari" element={<Protected roles={["teacher", "super_admin"]}><KujdestariDashboard /></Protected>} />
                        <Route path="/kujdestari/meetings" element={<Protected roles={["teacher", "super_admin"]}><KujdestariMeetings /></Protected>} />
                        <Route path="/kujdestari/discipline" element={<Protected roles={["teacher", "super_admin"]}><KujdestariDiscipline /></Protected>} />
                        <Route path="/kujdestari/end-of-year-report" element={<Protected roles={["teacher", "super_admin"]}><KujdestariEndOfYearReport /></Protected>} />

                        <Route path="/admin" element={<Protected roles={["super_admin"]}><AdminDashboard /></Protected>} />
                        <Route path="/admin/timetable" element={<Protected roles={["super_admin"]}><AdminTimetable /></Protected>} />
                        <Route path="/admin/notify" element={<Protected roles={["super_admin"]}><NotificationsSend /></Protected>} />

                        <Route path="/student" element={<Protected roles={["student"]}><StudentDashboard /></Protected>} />
                        <Route path="/student/timetable" element={<Protected roles={["student"]}><StudentTimetable /></Protected>} />
                        <Route path="/student/grades" element={<Protected roles={["student"]}><StudentGrades /></Protected>} />
                        <Route path="/student/lms" element={<Protected roles={["student"]}><StudentLMS /></Protected>} />

                        <Route path="/secretary" element={<Protected roles={["secretary", "super_admin"]}><SecretaryDashboard /></Protected>} />
                        <Route path="/secretary/class/:id" element={<Protected roles={["secretary", "super_admin"]}><SecretaryClass /></Protected>} />
                        <Route path="/secretary/staff" element={<Protected roles={["secretary", "super_admin"]}><SecretaryStaff /></Protected>} />

                        <Route path="/quality" element={<Protected roles={["quality_coordinator", "super_admin"]}><QualityDashboard /></Protected>} />

                        <Route path="/administrata" element={<Protected roles={["administrata", "super_admin"]}><AdministrataDashboard /></Protected>} />
                        <Route path="/administrata/students" element={<Protected roles={["administrata", "super_admin"]}><AdministrataStudents /></Protected>} />
                        <Route path="/administrata/appointments" element={<Protected roles={["administrata", "super_admin", "drejtoresha", "secretary"]}><AdministrataAppointments /></Protected>} />

                        <Route path="/sigurimi" element={<Protected roles={["sigurimi", "super_admin"]}><SigurimiDashboard /></Protected>} />
                        <Route path="/sigurimi/incident/:kind/:id" element={<Protected roles={["sigurimi", "super_admin"]}><SigurimiIncident /></Protected>} />
                        <Route path="/sigurimi/reports" element={<Protected roles={["sigurimi", "super_admin", "drejtoresha"]}><SigurimiReports /></Protected>} />

                        <Route path="/drejtoresha" element={<Protected roles={["drejtoresha", "super_admin"]}><DrejtoreshaDashboard /></Protected>} />
                        <Route path="/drejtoresha/collaborations" element={<Protected roles={["drejtoresha", "super_admin", "administrata"]}><DrejtoreshaCollaborations /></Protected>} />

                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </BrowserRouter>
                <Toaster />
            </AuthProvider>
        </div>
    );
}

export default App;
